/**
 * TypeaheadDropdown — SPD v5.83 Phase 2 (2026-07-14).
 * ─────────────────────────────────────────────────────
 * Renders the LLM classifier's response below the primary search
 * input as an intent-narrowing primitive. Six primitives ship in
 * this pass, driven by `data.layout_hint`:
 *   two_col_compare · card_grid · prose_and_sources · lens_grid ·
 *   step_by_step · calculator
 *
 * Design language matches /preview/primitives-mockup-v3 approved by
 * the operator (2026-07-14):
 *   · No container box · no card chrome · no headers
 *   · LLM-authored italic cyan `intro_line` above the chips
 *   · Chips are plain cyan text with hairline hover underline
 *   · Left-aligned to the search input's edge
 *
 * Callback contract:
 *   · onPickChip(label)        — a chip clicked; append to query
 *   · onPickCompletion(text)   — a completion clicked; replace query
 *   · onPickSource(source)     — Support Local / Big Players / Both
 *   · onPickFreetext(text)     — user typed something in Other [eg:]
 *
 * SPD v5.82 back-compat: if `completions` / `ambiguityAxes` are
 * passed in as legacy props (no `data` prop), fall through to a
 * minimal legacy render so existing callers don't break.
 */
import React, { useEffect } from 'react';
import {
  PrimitiveByLayoutHint,
  injectPrimitiveStyles,
} from '../typeahead/TypeaheadPrimitives';

export const TypeaheadDropdown = ({
  data,
  onPickChip,
  onPickCompletion,
  onPickSource,
  onPickFreetext,
  // SPD v5.82 legacy props — kept for callers that haven't migrated.
  completions,
  ambiguityAxes,
  loading = false,
  onPick,
  onPickAxis,
}) => {
  useEffect(() => { injectPrimitiveStyles(); }, []);

  // If the caller passed the new `data` prop, render the SPD v5.83
  // primitive tree. Otherwise fall through to the legacy render so
  // migrations can happen one call-site at a time.
  const usingLegacyProps = !data && (completions || ambiguityAxes);

  if (usingLegacyProps) {
    // Legacy render path — SPD v5.82 shape.
    return (
      <LegacyDropdown
        completions={completions || []}
        ambiguityAxes={ambiguityAxes || []}
        loading={loading}
        onPick={onPick}
        onPickAxis={onPickAxis}
      />
    );
  }

  if (!data) return null;

  // Loading state — only if the dropdown has nothing to show yet.
  const nothingToShow =
    !data.intro_line &&
    (!data.chips || data.chips.length === 0) &&
    (!data.completions || data.completions.length === 0);

  if (data.loading && nothingToShow) {
    return (
      <div className="mt-3 flex items-center gap-2.5" data-testid="typeahead-loading">
        <span className="inline-block h-3 w-3 rounded-full border-2 border-cyan-400/60 border-t-transparent animate-spin" />
        <span className="text-cyan-300/80 text-sm italic">Thinking&hellip;</span>
      </div>
    );
  }

  if (nothingToShow) return null;

  return (
    <div className="w-full ta-fade-slow" data-testid="typeahead-dropdown">
      <PrimitiveByLayoutHint
        data={data}
        onPickChip={(label) => {
          if (onPickChip) onPickChip(label);
          else if (onPickAxis) onPickAxis(label);  // legacy fall-through
        }}
        onPickCompletion={(text) => {
          if (onPickCompletion) onPickCompletion(text);
          else if (onPick) onPick(text);
        }}
        onPickSource={onPickSource}
        onPickFreetext={onPickFreetext}
      />
    </div>
  );
};

// Legacy render — SPD v5.82 shape (completions + ambiguity axes).
// Kept intact so any legacy call-site (or SPD v5.82-shaped cache
// row) still renders something sensible until the whole app is
// migrated. Delete on the next major SPD when nothing calls it.
const LegacyDropdown = ({ completions, ambiguityAxes, loading, onPick, onPickAxis }) => {
  if (loading && completions.length === 0 && ambiguityAxes.length === 0) {
    return (
      <div className="mt-3 px-4 py-3 flex items-center gap-2.5 rounded-xl border border-cyan-500/25 bg-slate-900/60" data-testid="typeahead-loading">
        <span className="inline-block h-3 w-3 rounded-full border-2 border-cyan-400/60 border-t-transparent animate-spin" />
        <span className="text-slate-300 text-sm">Thinking</span>
      </div>
    );
  }
  if (completions.length === 0 && ambiguityAxes.length === 0) return null;
  return (
    <div className="mt-3 rounded-xl border border-cyan-500/25 bg-slate-900/60 backdrop-blur-sm" data-testid="typeahead-dropdown-legacy">
      {completions.length > 0 && (
        <ul className="p-2 space-y-1">
          {completions.map((text, i) => (
            <li key={`${text}-${i}`}>
              <button
                type="button"
                onClick={() => onPick && onPick(text)}
                data-testid={`typeahead-completion-${i}`}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-100 hover:bg-cyan-500/10 hover:text-cyan-100 transition-colors"
              >
                {text}
              </button>
            </li>
          ))}
        </ul>
      )}
      {ambiguityAxes.length > 0 && (
        <div className="px-3 py-3 border-t border-slate-800/60 flex flex-wrap gap-2">
          {ambiguityAxes.map((axis) => (
            <button
              key={axis}
              type="button"
              onClick={() => onPickAxis && onPickAxis(axis)}
              data-testid={`typeahead-axis-${String(axis).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="px-2.5 py-1 rounded-full text-xs bg-slate-800/70 text-slate-200 hover:bg-cyan-500/20 hover:text-cyan-100 border border-slate-700/60 transition-colors"
            >
              {axis}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TypeaheadDropdown;
